import { LocalStorage } from 'quasar';
import type { RouteLocationNormalized } from 'vue-router';
import { notFoundHook } from 'src/router/hooks';
// Direct imports (not via src/store/main barrel) to keep the router out of
// the 60-store barrel chain. See CLAUDE.md "Build Optimization" section.
import { useAuthTokenStore } from 'src/store/User/auth_token.store';
import { isSupplierUser } from 'src/utils/UserDetails';

const routes = [
  {
    path: '/',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "login" */ 'src/pages/User/Login.vue'),
        beforeEnter: (to: any, from: any, next: any) => {
          // Handle token-based auto-login from URL params
          const queryToken = to.query.token;
          const queryOrgId = to.query.org_id;

          if (queryToken && queryOrgId) {
            // Redirect to token handler route for auto-login
            next({
              path: '/token-login',
              query: { token: queryToken, org_id: queryOrgId },
            });
            return;
          }

          // Check for a valid (non-expired) token before redirecting to dashboard
          const auth_token_store = useAuthTokenStore();
          if (!auth_token_store.isTokenExpired()) {
            next('/dashboard');
          } else {
            next();
          }
        },
      },
      {
        path: '/po-image',
        component: () => import(/* webpackChunkName: "core" */ 'src/components/ImageComponent.vue'),
      },
      {
        path: '/auth/:token/:source?',
        component: () => import(/* webpackChunkName: "auth" */ 'src/pages/validate-token.vue'),
        meta: {
          requiresAuth: false,
        },
      },
      {
        // BUSINESS VALUE: Public Document Status Page (Shareable Links)
        path: '/status/:documentId',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/PublicDocumentStatus.vue'),
        beforeEnter: (to: any, from: any, next: any) => {
          // Optional token validation for enhanced security
          const shareToken = to.query.token;
          if (!shareToken) {
            // Allow access without token for basic status (limited info)
            to.params.shareToken = null;
          } else {
            to.params.shareToken = shareToken;
          }
          next();
        },
        meta: {
          requiresAuth: false,
        },
      },
    ],
  },
  {
    path: '/login',
    component: () => import(/* webpackChunkName: "login" */ 'src/pages/User/Login.vue'),
  },
  {
    path: '/prepare-dashboard',
    component: () => import(/* webpackChunkName: "admin" */ 'src/pages/User/PrepareDashboard.vue'),
    // requiresAuth so the global guard's special case applies (public routes
    // short-circuit before it): it redirects to /login when the token is
    // missing, otherwise lets prepare-dashboard own cross-region reconciliation.
    meta: { requiresAuth: true },
  },
  {
    path: '/oauth/login',
    component: () => import(/* webpackChunkName: "login" */ 'src/pages/User/Login.vue'),
  },
  {
    path: '/forget-password',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/User/Forget-Password.vue'),
      },
    ],
  },
  {
    path: '/reset-password',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/User/Reset-Password.vue'),
      },
    ],
  },
  {
    // Set password page for new users (admin-created accounts)
    // Uses same backend endpoint as reset-password but with welcome messaging
    path: '/set-password',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/User/Set-Password.vue'),
      },
    ],
  },
  {
    path: '/register',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/User/Register/Register.vue'),
      },
    ],
  },
  {
    path: '/verify-email',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/User/Register/Verify-Email.vue'),
      },
    ],
  },
  {
    path: '/register-success',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/User/Register/Register-Success.vue'),
      },
    ],
  },
  {
    path: '/supplier/register',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "supplier" */ 'src/supplier/pages/Supplier/Register.vue'),
      },
    ],
  },
  {
    path: '/supplier/register/terms_and_conditions/:org_id',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "supplier" */ 'src/supplier/pages/Supplier/TermAndCondition.vue'),
      },
    ],
  },
  {
    path: '/supplier',
    component: () => import(/* webpackChunkName: "core" */ 'src/layouts/MainLayout.vue'),
    children: [
      {
        path: '/supplier/dashboard',
        component: () => import(/* webpackChunkName: "supplier" */ 'src/supplier/pages/dashboard.vue'),
        beforeEnter: (to: any, from: any, next: any) => {
          if (isSupplierUser()) {
            notFoundHook.execute(to, from);
            next('/not_found');
          } else {
            next();
          }
        },
        meta: {
          requiresAuth: true,
          pathname: 'supplier.dashboard',
          primary_breadcrumbs: [
            {
              label: 'supplier_supplier_dashboard',
              path: '/supplier/dashboard',
            },
          ],
        },
      },

      // WebSocket Debug Console (development tool)
      {
        path: '/debug-websocket',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/debug-websocket.vue'),
        meta: {
          requiresAuth: true,
          pathname: 'debug.websocket',
        },
      },

      // PWA Cache Diagnostic Page (for debugging blank screen issues)
      {
        path: '/debug/pwa-cache-status',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Debug/PWACacheStatus.vue'),
        meta: {
          requiresAuth: false,
          pathname: 'debug.pwa.cache',
        },
      },

      // dynamic route for demo
      {
        path: '/supplier/details',
        component: () => import(/* webpackChunkName: "supplier" */ 'src/supplier/layouts/SupplierWizardLayout.vue'),
        children: [
          {
            path: '/supplier/register-wizard/:oldId/:id',
            component: () => import(/* webpackChunkName: "supplier" */ 'src/supplier/pages/SupplierRegisterWizard.vue'),
            meta: {
              requiresAuth: true,
              pathname: 'supplier.register_wizard',
            },
          },
          {
            path: '/supplier/details/:oldId/:id',
            component: () => import(/* webpackChunkName: "supplier" */ 'src/supplier/pages/SupplierDetails.vue'),
            meta: {
              requiresAuth: true,
              pathname: 'supplier.details',
              primary_breadcrumbs: [
                {
                  label: 'supplier_supplier_dashboard',
                  path: '/supplier/dashboard',
                },
                {
                  label: 'form_builder_supplier_details',
                  path: '/supplier/details/:oldId/:id',
                },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    path: '/asn_shipment',
    component: () => import(/* webpackChunkName: "core" */ 'layouts/MainLayout.vue'),
    children: [
      {
        path: '/asn_shipment/dashboard',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/asn_shipment/pages/dashboard.vue'),
        meta: {
          primary_breadcrumbs: [
            {
              label: 'advance_shipment_dashboard',
              path: '/asn_shipment/dashboard',
            },
          ],
        },
      },
    ],
  },
  {
    path: '/invoice/dashboard',
    component: () => import(/* webpackChunkName: "core" */ 'layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Invoice/page/dashboard.vue'),
        meta: {
          primary_breadcrumbs: [
            { label: 'invoice_dashboard', path: '/invoice/dashboard' },
          ],
        },
      },
    ],
  },
  {
    // Hidden dev/QA route — visual demo for the L0–L4 validation
    // report panel (BOL-413 / BOL-418). Not linked from any sidebar.
    // Gated to non-customer environments only: prod / demo / sandbox
    // redirect to the dashboard so end-users on prod cannot reach
    // the synthetic-data demo.
    path: '/dev/validation-report-demo',
    beforeEnter: (_to: any, _from: any, next: any) => {
      const stage = process.env.APP_VUE_STAGE;
      if (stage === 'prod' || stage === 'demo' || stage === 'sandbox') {
        return next('/dashboard');
      }
      next();
    },
    component: () => import(/* webpackChunkName: "core" */ 'layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        component: () =>
          import(
            /* webpackChunkName: "validation-demo" */ 'src/pages/Dev/ValidationReportDemo.vue'
          ),
        meta: {
          primary_breadcrumbs: [
            { label: 'Validation Report Demo', path: '/dev/validation-report-demo' },
          ],
        },
      },
    ],
  },
  {
    path: '/invoice/details/:id',
    component: () => import(/* webpackChunkName: "core" */ 'layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Invoice/page/details.vue'),
        meta: {
          primary_breadcrumbs: [
            { label: 'invoice_dashboard', path: '/invoice/dashboard' },
            { label: 'invoice_details', path: '/invoice/details/:id' },
          ],
        },
      },
    ],
  },
  // Dead routes removed: /table_train, /table_train_v3, /ext-po-matching-v3 (zero references)

  // =========================================================================
  // ADMIN ROUTES - Under MainLayout (provides SideMenu)
  // =========================================================================
  {
    path: '/admin',
    component: () => import(/* webpackChunkName: "core" */ 'layouts/MainLayout.vue'),
    children: [
      // =====================================================================
      // SETTINGS ROUTES - Nested under /settings with SettingsLayout
      // =====================================================================
      {
        path: '/settings',
        component: () => import(/* webpackChunkName: "settings" */ 'src/layouts/SettingsLayout.vue'),
        meta: {
          requiresAuth: true,
        },
        children: [
      {
        path: '',
        name: 'settings-hub',
        component: () => import(/* webpackChunkName: "settings" */ 'src/pages/Settings/SettingsHub.vue'),
        meta: {
          primary_breadcrumbs: [{ label: 'settings', path: '/settings' }],
        },
      },
      {
        path: 'category/:id',
        name: 'settings-category',
        component: () => import(/* webpackChunkName: "settings" */ 'src/pages/Settings/SettingsCategoryView.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [{ label: 'settings', path: '/settings' }],
        },
      },
      {
        path: 'decision-trees',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/DecisionTrees/decisionTrees.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'decision_trees', path: '/settings/decision-trees' },
          ],
        },
      },
      {
        path: 'decision-tree-designer/:id?',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/DecisionTrees/decisionTree-designer.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'decision_trees', path: '/settings/decision-trees' },
            {
              label: 'decision_tree_designer',
              path: '/settings/decision-tree-designer/:id',
            },
          ],
        },
      },
      {
        path: 'watchdog',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Watchdog/watchdog_settings.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'watchdog', path: '/settings/watchdog' },
          ],
        },
      },
      {
        path: 'data-validation',
        component: () => import(/* webpackChunkName: "master-data" */ 'src/pages/Settings/Pages/MasterDataLookup.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: 'master-data',
        component: () => import(/* webpackChunkName: "master-data" */ 'src/pages/Settings/Pages/MasterDataLookup_V2.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'lookup_master_data',
              path: '/settings/master-data',
            },
          ],
        },
      },
      {
        path: 'toml-samples',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/RuleManagement/TomlList.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'rule_management',
              path: '/settings/toml-samples',
            },
          ],
        },
      },
      {
        // E-Invoice validation rules catalog (BOL-414/417).
        path: 'validation-rules',
        component: () =>
          import(
            /* webpackChunkName: "validation-rules" */ 'src/pages/Settings/Components/ValidationRules/List.vue'
          ),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'edocs_nav_label', path: '/settings/validation-rules' },
            {
              label: 'edocs_rules_nav_label',
              path: '/settings/validation-rules',
            },
          ],
        },
      },
      {
        // E-Invoice validation-finding → agent notification routing (BOL-430/431).
        path: 'notification-routing',
        component: () =>
          import(
            /* webpackChunkName: "notification-routing" */ 'src/pages/Settings/Components/DocNetAgents/List.vue'
          ),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'edocs_nav_label', path: '/settings/validation-rules' },
            {
              label: 'edocs_actions_nav_label',
              path: '/settings/notification-routing',
            },
          ],
        },
      },
      {
        // Back-compat: the page shipped briefly as /settings/docnet-agents
        // (named after the agents it routes to). Renamed to reflect its real
        // purpose — routing, not agent management (those live at /doc-net/agents).
        path: 'docnet-agents',
        redirect: { path: '/settings/notification-routing' },
      },
      {
        path: 'rule-management/:id?',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/RuleManagement/main.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'rule_management',
              path: '/settings/toml-samples',
            },
            { label: 'view_template', path: '/settings/rule-management/:id?' },
          ],
        },
      },
      {
        path: 'data-validation/create/:id?',
        component: () => import(/* webpackChunkName: "master-data" */ 'src/pages/Settings/Components/MasterDataLookup/CustomMasterData/main.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'lookup_master_data',
              path: '/settings/master-data',
            },
            { label: 'create', path: '/settings/data-validation/create' },
          ],
        },
      },
      {
        path: 'data-validation/create-preset',
        component: () => import(/* webpackChunkName: "master-data" */ 'src/pages/Settings/Components/MasterDataLookup/CustomMasterData/customPreset.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'lookup_master_data',
              path: '/settings/master-data',
            },
            {
              label: 'create_preset',
              path: '/settings/data-validation/create-preset',
            },
          ],
        },
      },
      {
        path: 'dashboard',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Settings/Pages/Dashboard.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'dashboard', path: '/settings/dashboard' },
          ],
        },
      },
      {
        path: 'export',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Export/export-page.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'export', path: '/settings/export' },
          ],
        },
      },
      {
        path: 'export/:id',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Export/manage-configuration.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'export', path: '/settings/export' },
            { label: 'configuration', path: '/settings/export/:id' },
          ],
        },
      },
      {
        path: 'list-values',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/list-of-values.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'list_of_values', path: '/settings/list-values' },
          ],
        },
      },
      {
        path: 'import',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/import.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'import', path: '/settings/import' },
          ],
        },
      },
      {
        path: 'company-information',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/companyInformation.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'company_information',
              path: '/settings/company-information',
            },
          ],
        },
      },
      {
        path: 'subscriptions',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/subscriptions.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'subscription_settings',
              path: '/settings/subscriptions',
            },
          ],
        },
      },
      {
        path: 'infrastructure',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/Infrastructure.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'infrastructure',
              path: '/settings/infrastructure',
            },
          ],
        },
      },
      {
        path: 'users',
        component: () => import(/* webpackChunkName: "admin" */ 'pages/Settings/Users/List.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'users', path: '/settings/users' },
          ],
        },
      },
      { path: 'user', redirect: 'users' },
      {
        path: 'user/:id',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/Settings/Users/UserDetail.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'users', path: '/settings/users' },
            { label: 'user_detail', path: '/settings/user/:id' },
          ],
        },
      },
      {
        path: 'login-analytics',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/Settings/Users/LoginAnalytics.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'users', path: '/settings/users' },
            { label: 'login_analytics', path: '/settings/login-analytics' },
          ],
        },
      },
      {
        path: 'api',
        redirect: 'integration',
      },
      {
        path: 'document-types',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Settings/Pages/document-type.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
          ],
        },
      },
      {
        path: 'email-templates',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/EmailTemplate/List.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'email_templates', path: '/settings/email-templates' },
          ],
        },
      },
      {
        path: 'email-templates/add',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/EmailTemplate/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'email_templates', path: '/settings/email-templates' },
            {
              label: 'add_new_template',
              path: '/settings/email-templates/add',
            },
          ],
        },
      },
      {
        path: 'email-templates/:id',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/EmailTemplate/Edit.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'email_templates', path: '/settings/email-templates' },
            { label: 'edit_template', path: '/settings/email-templates/:id' },
          ],
        },
      },
      {
        path: 'document-types/create',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/CreateDocType/Main.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            { label: 'create_doc', path: '/settings/document-types/create' },
          ],
        },
      },
      {
        path: 'fields/:type/:sub_doc_type?',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/field_setting.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            { label: 'fields', path: '/settings/fields/:type/:sub_doc_type?' },
          ],
        },
      },
      {
        path: 'fields/:type/:sub_doc_type?/master-data',
        component: () => import(/* webpackChunkName: "master-data" */ 'src/pages/Settings/Pages/LookUps/MasterData.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            { label: 'fields', path: '/settings/fields/:type/:sub_doc_type?' },
            {
              label: 'master_data_settings',
              path: '/settings/fields/:type/:sub_doc_type?/master-data',
            },
          ],
        },
      },
      {
        path: 'sub-document-types/:doc_key',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/sub-document-type.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            { label: 'sub_doc_type', path: '/settings/sub-document-types/:doc_key' },
          ],
        },
      },
      {
        path: 'custom-columns/:type',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/doc-type-table-columns.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            { label: 'custom_columns', path: '/settings/custom-columns/:type' },
          ],
        },
      },
      {
        name: 'record-script',
        path: 'record-script/:doc_type/:sub_doc_type_key?',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/script-record.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'sub_doc_type',
              path: '/settings/sub-document-types/:doc_type',
              requires_param: 'sub_doc_type_key',
            },
            {
              label: 'scripts',
              path: '/settings/manage-scripts/:doc_type/:sub_doc_type_key',
            },
            {
              label: 'create_script',
              path: '/settings/record-script/:doc_type/:sub_doc_type_key',
            },
          ],
        },
      },
      {
        path: 'model-training/:doc_type/:doc_key',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/model-training.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'train_model',
              path: '/settings/model-training/:doc_type/:doc_key',
            },
          ],
        },
      },
      {
        path: 'all-templates',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/LayoutBuilder/List.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'All templates', path: '/settings/document-types' },
          ],
        },
      },
      {
        path: 'custom-models',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Components/ModelsAndLabels/ModelsList.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: 'roles',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Roles/Groups.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'roles_groups', path: '/settings/roles' },
          ],
        },
      },
      {
        path: 'roles_deprecated',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Roles/Groups_deprecated.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'roles_groups', path: '/settings/roles_redesign' },
          ],
        },
      },
      {
        path: 'access-control/:group_name/:id',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Roles/AccessControl/List.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'roles_groups', path: '/settings/roles' },
            {
              label: 'access_control',
              path: '/settings/access-control/:group_name/:id',
            },
          ],
        },
      },
      {
        path: 'access-control/detail/:group_name/:doc_type/:id',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Roles/AccessControl/Detail.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'roles_groups', path: '/settings/roles' },
            {
              label: 'access_control',
              path: '/settings/access-control/:group_name/:id',
            },
            {
              label: ':group_name',
              path: '/settings/access-control/detail/:group_name/:doc_type/:id',
            },
          ],
        },
      },
      {
        path: 'classify-extract',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/ClassifyExtract.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'classify_extract',
              path: '/settings/classify-extract',
            },
          ],
        },
      },
      {
        path: 'ai-prompt',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/ai_prompt.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'ai_prompt', path: '/settings/ai-prompt' },
          ],
        },
      },
      {
        path: 'ocr-settings',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/ocrSettings.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'ocr', path: '/settings/ocr-settings' },
          ],
        },
      },
      {
        path: 'sub-organizations',
        component: () => import(/* webpackChunkName: "config" */ 'pages/Settings/SubOrganizations/List.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'sub_organizations', path: '/settings/sub-organizations' },
          ],
        },
      },
      {
        path: 'document-expiry',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Settings/Pages/document-general.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'general_document_settings',
              path: '/settings/document-expiry',
            },
          ],
        },
      },
      {
        path: 'invoice-calculation',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/invoice-calculation.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'due_date_calculation_settings',
              path: '/settings/invoice-calculation',
            },
          ],
        },
      },
      {
        path: 'supplier/group',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierGroup/List.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'supplierGroup',
              path: '/settings/supplier/group',
            },
          ],
        },
      },
      {
        path: 'supplier/user-group-mapping',
        component: () => import(/* webpackChunkName: "admin" */ 'src/pages/Settings/UserAndSupplierGroupMapping/UserAndSupplierGroupMapping.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'user_supplier_mapping',
              path: '/settings/supplier/user-group-mapping',
            },
          ],
        },
      },
      {
        path: 'supplier/policy-privacy-statement',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierPolicyPrivacyStatement/SupplierPolicyPrivacyStatement.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'policy_privacy_statement',
              path: '/settings/supplier/policy-privacy-statement',
            },
          ],
        },
      },
      {
        path: 'supplier/export-configuration',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierExportConfiguration/detail.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'export_configuration',
              path: '/settings/supplier/export-configuration',
            },
          ],
        },
      },
      {
        path: 'supplier/layouts-builder',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierLayouts/List.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'supplier_layout',
              path: '/settings/supplier/layouts-builder',
            },
          ],
        },
      },
      {
        path: 'supplier/layouts-builder/default-fields',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierLayouts/DefaultFields.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'supplier_layout',
              path: '/settings/supplier/layouts-builder',
            },
            {
              label: 'fields',
              path: '/settings/supplier/layouts-builder/default-fields',
            },
          ],
        },
      },
      {
        path: 'supplier/layouts-builder/:layout_type',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierLayouts/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'supplier_layout',
              path: '/settings/supplier/layouts-builder',
            },
            {
              label: ':layout_type',
              path: '/settings/supplier/layouts-builder/:layout_type',
            },
          ],
        },
      },
      {
        path: 'supplier/email-templates',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierEmailTemplate/List.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'supplier_email_templates',
              path: '/settings/supplier/email-templates',
            },
          ],
        },
      },
      {
        path: 'supplier/email-templates/add',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierEmailTemplate/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'email_templates',
              path: '/settings/supplier/email-templates',
            },
            {
              label: 'add_new_template',
              path: '/settings/supplier/email-templates/add',
            },
          ],
        },
      },
      {
        path: 'supplier/email-templates/:id',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierEmailTemplate/Edit.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'email_templates',
              path: '/settings/supplier/email-templates',
            },
            {
              label: 'edit_template',
              path: '/settings/supplier/email-templates/:id',
            },
          ],
        },
      },
      {
        path: 'supplier/general',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/SupplierGeneral/SupplierGeneral.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'supplier_general_setting',
              path: '/settings/supplier/general',
            },
          ],
        },
      },
      {
        path: 'cache-management',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/CacheManagement.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'cache_management', path: '/settings/cache-management' },
          ],
        },
      },
      {
        path: 'fulltext',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/FulltextSettings.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'fulltext_settings', path: '/settings/fulltext' },
          ],
        },
      },
      {
        path: 'performance-metrics',
        component: () => import(/* webpackChunkName: "reports" */ 'src/pages/Report/Performance.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: false,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'performance_metrics',
              path: '/settings/performance-metrics',
            },
          ],
        },
      },
      {
        path: 'modules',
        redirect: '/settings/modules/order-finance',
      },
      {
        path: 'modules/order-finance',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/modules-order-finance.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'module', path: '/settings/modules' },
            { label: 'order_finance', path: '/settings/modules/order-finance' },
          ],
        },
      },
      {
        path: 'modules/dashboards-analytics',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/modules-dashboards-analytics.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'module', path: '/settings/modules' },
            { label: 'dashboards_analytics', path: '/settings/modules/dashboards-analytics' },
          ],
        },
      },
      {
        path: 'modules/document-integration',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/modules-document-integration.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'module', path: '/settings/modules' },
            { label: 'document_integration', path: '/settings/modules/document-integration' },
          ],
        },
      },
      // Agent Setup removed — managed in /doc-net/agents
      {
        path: 'doc-status-notifications',
        component: () => import(/* webpackChunkName: "config" */ 'pages/Settings/DocStatusNotifications/List.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'doc_status_notifications',
              path: '/settings/doc-status-notifications',
            },
          ],
        },
      },
      {
        path: 'api-licenses',
        component: () => import(/* webpackChunkName: "config" */ 'pages/Settings/Licenses/ApiLicenses.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'pages_settings_api_licenses',
              path: '/settings/api-licenses',
            },
          ],
        },
      },
      {
        path: 'card-sdk',
        component: () => import(/* webpackChunkName: "config" */ 'pages/WorkflowBuilder/CardSDK.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'Card SDK', path: '/settings/card-sdk' },
          ],
        },
      },
      {
        path: 'log-settings',
        component: () => import(/* webpackChunkName: "config" */ 'pages/Settings/Logs/Log-Settings.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'pages_log_settings', path: '/settings/log-settings' },
          ],
        },
      },
      {
        path: 'data-sync-settings',
        component: () => import(/* webpackChunkName: "config" */ 'pages/Settings/Logs/Data-Synchronization.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'pages_data_sync_settings',
              path: '/settings/data-sync-settings',
            },
          ],
        },
      },
      {
        path: 'event-logs',
        component: () =>
          import(
            /* webpackChunkName: "activity-logs" */ 'src/pages/ActivityLogs/ActivityLogs.vue'
          ),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'title',
              path: '/settings/event-logs',
            },
          ],
        },
      },
      {
        path: 'analytics',
        name: 'Settings Analytics',
        component: () =>
          import(
            /* webpackChunkName: "analytics" */ 'src/pages/Settings/Analytics/Analytics.vue'
          ),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'analytics',
              path: '/settings/analytics',
            },
          ],
        },
      },
      {
        path: 'event-logs/detail/:id',
        name: 'activity-log-detail',
        component: () =>
          import(
            /* webpackChunkName: "activity-logs" */ 'src/pages/ActivityLogs/ActivityLogDetail.vue'
          ),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'title',
              path: '/settings/event-logs',
            },
            {
              label: 'activity_logging_details',
              path: '/settings/event-logs/detail',
            },
          ],
        },
      },
      {
        path: 'access-audit',
        component: () =>
          import(
            /* webpackChunkName: "admin" */ 'src/pages/Settings/AuditTrail/AccessAudit.vue'
          ),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'access_audit',
              path: '/settings/access-audit',
            },
          ],
        },
      },
      {
        path: 'integration',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/integration.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'integration',
              path: '/settings/integration',
            },
          ],
        },
      },
      {
        path: 'doc-type-settings/:doc_type/:ai_prompt?',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Components/DocTypes/AdvanceSettings.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'more_settings',
              path: '/settings/doc-type-settings/:doc_type',
            },
          ],
        },
      },
      {
        path: 'po-match-rules/:doc_type',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Components/DocTypes/POMatch/Rules.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            { label: 'document_types', path: '/settings/document-types' },
            { label: 'more_settings', path: '/settings/doc-type-settings/:doc_type' },
            { label: 'PO_MATCH_RULES', path: '/settings/po-match-rules/:doc_type' },
          ],
        },
      },
      {
        path: 'manage-scripts/:doc_type/:sub_doc_type?',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/manage-scripts.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'sub_doc_type',
              path: '/settings/sub-document-types/:doc_type',
              requires_param: 'sub_doc_type',
            },
            {
              label: 'scripts',
              path: '/settings/manage-scripts/:doc_type/:sub_doc_type',
            },
          ],
        },
      },
      {
        path: 'manage-xslt/:doc_type',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Pages/xslt.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            { label: 'E-Doc', path: '/settings/manage-xslt/:doc_type' },
          ],
        },
      },
      {
        path: 'manage-xslt/:doc_type/:id/:e_doc_type',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Components/XSLT/detail.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'e_doc_attributes',
              path: '/settings/manage-xslt/:doc_type?tab=:e_doc_type',
            },
            {
              label: 'e_doc_detail',
              path: '/settings/manage-xslt/:doc_type/:id/:e_doc_type',
            },
          ],
        },
      },
      {
        path: 'record-xslt/:doc_type/:e_doc_type',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/Settings/Components/XSLT/add-new-edi.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'E-Doc',
              path: '/settings/manage-xslt/:doc_type/:id/:e_doc_type',
            },
          ],
        },
      },
      {
        path: 'doc-type-settings/pdf-layout',
        component: () =>
          import(/* webpackChunkName: "config" */ 'src/pages/PdfTemplateLayout/PdfTemplateLayout.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'more_settings',
              path: '/settings/doc-type-settings/:doc_type',
            },
            {
              label: 'template_editor',
              path: '/settings/doc-type-settings/pdf-layout',
            },
          ],
        },
      },

      // ─────────────────────────────────────────────────────────────────────
      // Backward compatibility redirects: old underscore/abbreviated paths
      // ─────────────────────────────────────────────────────────────────────

      // Organization & Access
      { path: 'sub_organizations', redirect: '/settings/sub-organizations' },
      { path: 'access_control/:group_name/:id', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/access-control/${to.params.group_name}/${to.params.id}`, query: to.query, hash: to.hash }) },
      { path: 'access_control/detail/:group_name/:doc_type/:id', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/access-control/detail/${to.params.group_name}/${to.params.doc_type}/${to.params.id}`, query: to.query, hash: to.hash }) },

      // Document Processing
      { path: 'document', redirect: '/settings/document-types' },
      { path: 'document-create', redirect: '/settings/document-types/create' },
      { path: 'document_general', redirect: '/settings/document-expiry' },
      { path: 'sub_doc_type/:doc_key', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/sub-document-types/${to.params.doc_key}`, query: to.query, hash: to.hash }) },
      { path: 'custom_columns/:type', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/custom-columns/${to.params.type}`, query: to.query, hash: to.hash }) },
      { path: 'record_script/:doc_type', redirect: (to: RouteLocationNormalized) => {
        const { sub_doc_type_key, ...rest } = to.query;
        const subPath = typeof sub_doc_type_key === 'string' && sub_doc_type_key ? `/${sub_doc_type_key}` : '';
        return { path: `/settings/record-script/${to.params.doc_type}${subPath}`, query: rest, hash: to.hash };
      } },
      { path: 'model_training/:doc_type/:doc_key', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/model-training/${to.params.doc_type}/${to.params.doc_key}`, query: to.query, hash: to.hash }) },
      { path: 'manage_scripts/:doc_type/:sub_doc_type?', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/manage-scripts/${to.params.doc_type}${to.params.sub_doc_type ? '/' + to.params.sub_doc_type : ''}`, query: to.query, hash: to.hash }) },
      { path: 'manage_xslt/:doc_type', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/manage-xslt/${to.params.doc_type}`, query: to.query, hash: to.hash }) },
      { path: 'manage_xslt/:doc_type/:id/:e_doc_type', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/manage-xslt/${to.params.doc_type}/${to.params.id}/${to.params.e_doc_type}`, query: to.query, hash: to.hash }) },
      { path: 'record_xslt/:doc_type/:e_doc_type', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/record-xslt/${to.params.doc_type}/${to.params.e_doc_type}`, query: to.query, hash: to.hash }) },
      { path: 'doc_type_settings/pdf-layout', redirect: '/settings/doc-type-settings/pdf-layout' },
      { path: 'doc_type_settings/:doc_type/:ai_prompt?', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/doc-type-settings/${to.params.doc_type}${to.params.ai_prompt ? '/' + to.params.ai_prompt : ''}`, query: to.query, hash: to.hash }) },
      { path: 'ocr_settings', redirect: '/settings/ocr-settings' },
      { path: 'decision_trees', redirect: '/settings/decision-trees' },
      { path: 'decisionTree_designer/:id?', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/decision-tree-designer${to.params.id ? '/' + to.params.id : ''}`, query: to.query, hash: to.hash }) },
      { path: 'ai_prompt', redirect: '/settings/ai-prompt' },
      { path: 'all_templates', redirect: '/settings/all-templates' },
      { path: 'fields/:type/:sub_doc_type?/master_data', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/fields/${to.params.type}${to.params.sub_doc_type ? '/' + to.params.sub_doc_type : ''}/master-data`, query: to.query, hash: to.hash }) },

      // Communication & Notifications
      { path: 'doc_status_notifications', redirect: '/settings/doc-status-notifications' },

      // System & Administration
      { path: 'apilicenses', redirect: '/settings/api-licenses' },
      { path: 'cache_management', redirect: '/settings/cache-management' },
      { path: 'log_settings', redirect: '/settings/log-settings' },
      { path: 'data_sync_settings', redirect: '/settings/data-sync-settings' },
      { path: 'performance_metrics', redirect: '/settings/performance-metrics' },

      // Supplier
      { path: 'supplier/email_templates', redirect: '/settings/supplier/email-templates' },
      { path: 'supplier/add_email_templates', redirect: '/settings/supplier/email-templates/add' },
      { path: 'supplier/edit_email_templates/:id', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/supplier/email-templates/${to.params.id}`, query: to.query, hash: to.hash }) },
      { path: 'supplier/layouts_builder', redirect: '/settings/supplier/layouts-builder' },
      { path: 'supplier/layouts_builder/default_fields', redirect: '/settings/supplier/layouts-builder/default-fields' },
      { path: 'supplier/layouts_builder/:layout_type', redirect: (to: RouteLocationNormalized) => ({ path: `/settings/supplier/layouts-builder/${to.params.layout_type}`, query: to.query, hash: to.hash }) },
      { path: 'supplier/export_configuration', redirect: '/settings/supplier/export-configuration' },
      { path: 'supplier/user_supplier_group_mapping', redirect: '/settings/supplier/user-group-mapping' },
      { path: 'supplier/policy_privacy_statement', redirect: '/settings/supplier/policy-privacy-statement' },
    ],
  },
  // Backward compatibility redirect
  {
    path: '/setting',
    redirect: '/settings',
    meta: { requiresAuth: true },
  },

  // =====================================================================
  // Non-settings routes (dashboard, workflows, etc.)
  // =====================================================================
  {
        path: '/dashboard/:force_restart?',
        component: () => import(/* webpackChunkName: "core" */ 'pages/dashboard.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [{ label: 'dashboard', path: '/dashboard' }],
        },
      },
      // App Index / Sitemap — searchable index of all pages and features
      {
        path: '/sitemap',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/AppIndex/AppIndex.vue'),
        beforeEnter: (to: any, from: any, next: any) => {
          if (isSupplierUser()) {
            notFoundHook.execute(to, from);
            next('/not_found');
          } else {
            next();
          }
        },
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'sitemap', path: '/sitemap' },
          ],
        },
      },
      // Email page moved to /doc-net/email
      {
        path: '/ideas',
        component: () => import(/* webpackChunkName: "ideas" */ 'src/pages/Ideas/IdeaBoard.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'idea_board', path: '/ideas' }
          ],
        },
      },
      {
        path: '/tests',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Tests/TestsPage.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'tests', path: '/tests' }
          ],
        },
      },
      {
        path: '/tests/statistics-dashboard',
        component: () =>
          import(
            /* webpackChunkName: "test-statistics" */ 'src/pages/Tests/StatisticsDashboard/StatisticsDashboard.vue'
          ),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'tests', path: '/tests' },
            { label: 'statistics_dashboard', path: '/tests/statistics-dashboard' },
          ],
        },
      },
      {
        path: '/tests/:serviceName',
        component: () => import(/* webpackChunkName: "test-details" */ 'src/pages/Tests/TestDetailsPage.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'tests', path: '/tests' },
            { label: ':serviceName', path: '/tests/:serviceName' }
          ],
        },
      },
      // Dead route removed: /column-sort-example (zero references)
      // Removed: /dashboard-grouping and /dashboard-v1 (Dashboard V2 / Grouping feature)
      {
        path: '/workflowsdashboard',
        component: () => import(/* webpackChunkName: "config" */ 'pages/WorkflowBuilder/workflowDashboard.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'workflowsdashboard', path: '/workflowsdashboard' },
          ],
        },
      },
      // Dead routes removed: /workflows, /workflows_task_manager (now tabs on /workflowsdashboard)
      {
        path: '/my_workflow/:doc_type/:id',
        component: () => import(/* webpackChunkName: "config" */ 'pages/WorkflowBuilder/workflow-designer.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'workflowsdashboard', path: '/workflowsdashboard' },
            { label: 'my_workflow', path: '/my_workflow/:doc_type/:id' },
          ],
        },
      },
      {
        path: '/workflow_designer/:id?',
        component: () => import(/* webpackChunkName: "config" */ 'pages/WorkflowBuilder/workflow-designer-v2.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'workflowsdashboard', path: '/workflowsdashboard' },
            { label: 'workflow_builder', path: '/workflow_designer/:id' },
          ],
        },
      },
      {
        path: '/advanced_workflow_designer/:id?',
        component: () => import(/* webpackChunkName: "config" */ 'pages/WorkflowBuilder/workflow-designer-advanced.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'workflowsdashboard', path: '/workflowsdashboard' },
            { label: 'workflow_builder', path: '/advanced_workflow_designer/:id' },
          ],
        },
      },
      {
        path: '/workflow_logs/:id?',
        component: () => import(/* webpackChunkName: "config" */ 'src/pages/WorkflowBuilder/workflowLogs/workflowLogsv2.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'workflowsdashboard', path: '/workflowsdashboard' },
            {
              label: 'workflow_builder',
              path: '/advanced_workflow_designer/:id',
            },
          ],
        },
      },
      {
        path: '/template_designer/:id?',
        component: () => import(/* webpackChunkName: "config" */ 'pages/WorkflowBuilder/workflow-designer-v2.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'workflowsdashboard', path: '/workflowsdashboard' },
            { label: 'template_designer', path: '/template_designer/:id' },
          ],
        },
      },
      {
        path: '/receipt',
        component: () => import(/* webpackChunkName: "core" */ 'pages/Receipt.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      // Dead route removed: /components (zero references)
      {
        path: '/shadcn-showcase',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/ShadcnShowcase.vue'),
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: '/notification-test',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/NotificationTest.vue'),
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: '/document/:id',
        component: () => import(/* webpackChunkName: "core" */ 'pages/document_view.vue'),
        // this page only serves as a redirect to have a static link to the document
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/field_validation_v1/:id',
        component: () =>
          import(/* webpackChunkName: "validation" */ 'pages/ValidationScreen/field-validation-view.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'field_validation', path: '/field_validation_v1/:id' },
          ],
          tasks_breadcrumbs: [
            { label: 'tasks', path: '/tasks' },
            { label: 'field_validation', path: '/field_validation_v1/:id' },
          ],
        },
      },
      {
        path: '/field_validation/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-medi-order.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'field_validation', path: '/field_validation/:id' },
          ],
        },
      },
      {
        path: '/medi_order_v3/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/MediOrder/Screen-medi-order-v3.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'medi_order', path: '/medi_order_v3/:id' },
          ],
        },
      },
      {
        path: '/tbl_ext_v3/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-tbl-extraction.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'field_validation', path: '/field_validation_v1/:id' },
            { label: 'table_extraction', path: '/tbl_ext_v3/:id' },
          ],
        },
      },
      {
        path: '/tbl_training/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/TableTraining/main.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'field_validation', path: '/field_validation_v1/:id' },
            { label: 'table_training', path: '/tbl_training/:id' },
          ],
        },
      },
      {
        path: '/certificate-of-analysis',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/CertificateOfAnalysis/page/dashboard.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'coa_dashboard', path: '/certificate-of-analysis' },
          ],
        },
      },
      {
        path: '/certificate-of-analysis/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/CertificateOfAnalysis/Screen-coa.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'coa_dashboard', path: '/certificate-of-analysis' },
            {
              label: 'certificate_of_analysis',
              path: '/certificate-of-analysis/:id',
            },
          ],
        },
      },
      {
        path: '/po-matching-v3/:id?/:P0?',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-po-matching-v3.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'field_validation', path: '/field_validation_v1/:id' },
            { label: 'po_matching', path: '/po-matching-v3/:id?/:P0?' },
          ],
        },
      },
      {
        path: '/po-matching/:id?/:P0?', component: () => import(/* webpackChunkName: "validation" */ 'src/pages/PurchaseOrders/ServerSideMatching/PO-matching.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            {label: 'dashboard', path: '/dashboard'},
            {label: 'field_validation', path: '/field_validation_v1/:id'},
            {label: 'po_matching', path: '/po-matching/:id?/:P0?'},
          ],
        },
      },
      {
        path: '/quote-details/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/QuoteDetails/QuoteDetails.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'field_validation', path: '/field_validation_v1/:id' },
            { label: 'quote_details', path: '/quote-details/:id' },
          ],
        },
      },
      {
        path: '/vertex-details/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/VertexDetails.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'vertex_tax_verification', path: '/vertex-details/:id' },
          ],
        },
      },
      {
        path: '/document_pdf/:name/:id',
        component: () => import(/* webpackChunkName: "core" */ 'pages/Document-Pdf.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'field_validation', path: '/field_validation_v1/:id' },
            { label: 'PDF', path: '/document_pdf/:name/:id' },
          ],
        },
      },
      {
        path: '/document_error/:id',
        component: () => import(/* webpackChunkName: "core" */ 'pages/Document-Error.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'error', path: '/document_error/:id' },
          ],
        },
      },
      {
        path: '/document_classifiy/:id',
        component: () => import(/* webpackChunkName: "core" */ 'pages/Document-Classifiy.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'classification', path: '/document_classifiy/:id' },
          ],
        },
      },
      {
        path: '/scan',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/app_scanner.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/report',
        component: () => import(/* webpackChunkName: "reports" */ 'src/pages/Report/main.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [{ label: 'report', path: '/report' }],
        },
      },
      {
        path: '/analytics-dashboard', component: () => import(/* webpackChunkName: "reports" */ 'src/pages/AnalyticsDashboard/AnalyticsDashboard.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            {label: 'analytics_dashboard', path: '/analytics-dashboard'},
          ],
        }
      },
      // shipment order list route
      {
        path: '/shipment-order',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/ShipmentOrder/pages/Dashboard.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'shipment_order_dashboard', path: '/shipment-order' },
          ],
        },
      },
      // purchase order list route
      {
        path: '/purchase-order',
        component: () => import(/* webpackChunkName: "supplier" */ 'src/supplier/pages/PurchaseOrderDashboard.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'purchase_order_dashboard', path: '/purchase-order' },
          ],
        },
      },
      // Doc Operator Route
      {
        path: '/doc-operator',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/docOperator.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'doc_operator', path: '/doc-operator' },
          ],
        },
      },
      {
        path: '/supplier-statistic',
        component: () => import(/* webpackChunkName: "reports" */ 'src/pages/supplierDeliveryStatistic.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'supplier_statistics', path: '/supplier-statistic' },
          ],
        },
      },
      // Doc Net Route
      {
        path: '/supplier-statistic',
        component: () => import(/* webpackChunkName: "reports" */ 'src/pages/supplierDeliveryStatistic.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'supplier_statistics', path: '/supplier-statistic' },
          ],
        },
      },
      // DocNet Task Management System
      {
        path: '/doc-net',
        component: () => import(/* webpackChunkName: "docnet" */ 'src/layouts/DocNetLayout.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [{ label: 'doc_net', path: '/doc-net' }],
        },
        children: [
          {
            path: '',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Dashboard/DocNetDashboard.vue'),
          },
          {
            path: 'issues',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Issues/DocNetIssueList.vue'),
          },
          {
            path: 'issues/new',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Issues/DocNetIssueCreate.vue'),
          },
          {
            path: 'issues/:id',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Issues/DocNetIssueDetail.vue'),
          },
          {
            path: 'missions',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Missions/DocNetMissionList.vue'),
          },
          {
            path: 'missions/:id',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Missions/DocNetMissionDetail.vue'),
          },
          {
            path: 'missions/:id/timeline',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Missions/DocNetMissionTimeline.vue'),
          },
          {
            path: 'projects',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Projects/DocNetProjectList.vue'),
          },
          {
            path: 'projects/:id',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Projects/DocNetProjectDetail.vue'),
          },
          {
            path: 'mission-control',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/MissionControl/MissionControlPage.vue'),
          },
          {
            path: 'inbox',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Inbox/DocNetInbox.vue'),
          },
          {
            path: 'inbox/:id',
            redirect: { path: '/doc-net/inbox' },
          },
          {
            path: 'email',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Email/DocNetEmail.vue'),
          },
          {
            path: 'issues/:id/runs/:runId',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Issues/DocNetRunDetail.vue'),
          },
          {
            path: 'agents',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Agents/DocNetAgentList.vue'),
          },
          {
            path: 'agents/:id',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Agents/DocNetAgentProfile.vue'),
          },
          {
            path: 'org-chart',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/OrgChart/DocNetOrgChart.vue'),
          },
          {
            path: 'activities',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Activities/DocNetActivities.vue'),
          },
          {
            path: 'settings',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/Settings/DocNetSettings.vue'),
          },
          {
            path: 'link-teams',
            component: () => import(/* webpackChunkName: "docnet" */ 'src/pages/DocNet/TeamsLinkCode/TeamsLinkCodePage.vue'),
          },
        ],
      },
      {
        path: '/full-search',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/FullSearch/list-box.vue'),
        meta: {
          requiresAuth: true,
          blockedForSupplier: true,
          primary_breadcrumbs: [{ label: 'open_search', path: '/full-search' }],
        },
      },
      {
        path: '/graph-explorer',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/GraphExplorer/index.vue'),
        meta: {
          requiresAuth: true,
          blockedForSupplier: true,
          primary_breadcrumbs: [{ label: 'knowledge_graph', path: '/graph-explorer' }],
        },
      },
      // purchase order details route
      {
        path: '/purchase-order-detail/:purchase_number',
        component: () => import(/* webpackChunkName: "supplier" */ 'src/supplier/pages/PurchaseOrderDetail.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'purchase_order_dashboard', path: '/purchase-order' },
            {
              label: 'purchase_order',
              path: '/purchase-order-detail/:purchase_number',
            },
          ],
        },
      },
      // Dead route removed: /scroll-paggination (zero references)
      {
        // BUSINESS VALUE: Processing Analytics Dashboard
        path: '/analytics/processing',
        component: () =>
          import(
            /* webpackChunkName: "reports" */ 'src/pages/BusinessIntelligence/ProcessingAnalyticsDashboard.vue'
          ),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [
            { label: 'analytics', path: '/analytics/processing' },
          ],
        },
      },
      {
        path: '/support',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/supportPage.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [{ label: 'support', path: '/support' }],
        },
      },
      {
        path: '/system-logs',
        component: () =>
          import(
            /* webpackChunkName: "system-logs" */ 'src/pages/SystemLogs/SystemLogs.vue'
          ),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          primary_breadcrumbs: [{ label: 'system_logs', path: '/system-logs' }],
        },
      },
      // Dead route removed: /demo (zero references)
      {
        path: '/summary/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Approval/Summery.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/ready-for-approval/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-ready-for-approval.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'approval', path: '/ready-for-approval/:id' },
          ],
        },
      },
      {
        path: '/ready-for-approval-v2/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-ready-for-approval-v2.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'approval', path: '/ready-for-approval-v2/:id' },
          ],
        },
      },
      {
        path: '/ready_for_approval_v3/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-ready-for-approval-v3.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'approval', path: '/ready_for_approval_v3/:id' },
          ],
        },
      },
      {
        path: '/in_review/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-ready-for-approval-v2.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/in-review-v2/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-ready-for-approval-v3.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/ready-for-second-approval-v2/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-ready-for-approval-v2.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'approval', path: '/ready-for-second-approval-v2/:id' },
          ],
        },
        beforeEnter: (to: any, from: any, next: any) => {
          to.params.second_approval = 'true';
          next();
        },
      },
      {
        path: '/ready-for-second-approval-v3/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-ready-for-approval-v3.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'approval', path: '/ready-for-second-approval-v3/:id' },
          ],
        },
        beforeEnter: (to: any, from: any, next: any) => {
          to.params.second_approval = 'true';
          next();
        },
      },
      {
        path: '/rejected/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-rejected.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'rejected', path: '/rejected/:id' },
          ],
        },
      },
      {
        path: '/ready-for-second-approval/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/Screen-ready-for-approval.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'approval', path: '/ready-for-second-approval/:id' },
          ],
        },
        beforeEnter: (to: any, from: any, next: any) => {
          to.params.second_approval = 'true';
          next();
        },
      },
      // Unified Layout Builder — canonical route
      {
        path: '/layout-builder/:doc_type/:layout_type?',
        component: () => import(/* webpackChunkName: "config" */ 'src/formBuilder/main.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'layout_builder_module',
              path: '/layout-builder/:doc_type/:layout_type?',
            },
          ],
          'settings_doc-type-settings_breadcrumbs': [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            {
              label: 'more_settings',
              path: '/settings/doc-type-settings/:doc_type',
            },
            {
              label: 'layout_builder_module',
              path: '/layout-builder/:doc_type/:layout_type?',
            },
          ],
          settings_sub_doc_type_breadcrumbs: [
            { label: 'settings', path: '/settings' },
            {
              label: 'document_types',
              path: '/settings/document-types',
            },
            { label: 'sub_doc_type', path: '/settings/sub-document-types/:doc_type' },
            {
              label: 'layout_builder_module',
              path: '/layout-builder/:doc_type/:layout_type?',
            },
          ],
        },
      },
      // Legacy redirects — keep old URLs working
      {
        path: '/manage_layout_builder/:doc_type/:id?',
        redirect: (to: RouteLocationNormalized) => {
          const { doc_type, id } = to.params;
          const query = { ...to.query } as Record<string, string>;
          if (id) query.id = id as string;
          return { path: `/layout-builder/${doc_type}`, query, hash: to.hash };
        },
      },
      {
        path: '/manage_approval_layout_builder_v2/:doc_type/:layout_type/:id?',
        redirect: (to: RouteLocationNormalized) => {
          const { doc_type, layout_type, id } = to.params;
          const query = { ...to.query } as Record<string, string>;
          if (id) query.id = id as string;
          return { path: `/layout-builder/${doc_type}/${layout_type}`, query, hash: to.hash };
        },
      },
      {
        path: '/vertex_layout_builder/:doc_type/:layout_type/:id?',
        redirect: (to: RouteLocationNormalized) => {
          const { doc_type, layout_type, id } = to.params;
          const query = { ...to.query } as Record<string, string>;
          if (id) query.id = id as string;
          return { path: `/layout-builder/${doc_type}/${layout_type}`, query, hash: to.hash };
        },
      },
      {
        path: '/manage_approval_layout_builder/:doc_type/:layout_type/:id?',
        redirect: (to: RouteLocationNormalized) => {
          const { doc_type, layout_type, id } = to.params;
          const query = { ...to.query } as Record<string, string>;
          if (id) query.id = id as string;
          return { path: `/layout-builder/${doc_type}/${layout_type}`, query, hash: to.hash };
        },
      },
      // {
      //   path: '/manage_layout_builder/:doc_type/:id?', component: () => import('src/formBuilder/main.vue'),
      //   meta: {
      //     requiresAuth: true,
      //   }
      // },
      {
        path: '/flow',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/FlowCharts/main_flow.vue'),
      },
      // { path: '/flow_demo', component: () => import('src/pages/FlowCharts/demo-flow.vue') },
      {
        path: '/doc_flow/:id',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/FlowCharts/visual_flow_page.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'document_flow', path: '/doc_flow/:id' },
          ],
        },
      },
      {
        path: '/doc_script_dummy/:type',
        component: () => import(/* webpackChunkName: "core" */ 'src/components/document/script/script-screen-dummy.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/accounting/:id',
        component: () => import(/* webpackChunkName: "validation" */ 'src/pages/AutoAccounting/Detail.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'dashboard', path: '/dashboard' },
            { label: 'field_validation', path: '/field_validation_v1/:id' },
            { label: 'accounting', path: '/accounting/:id' },
          ],
        },
      },
      {
        path: '/accounting_range',
        component: () => import(/* webpackChunkName: "validation" */ 'src/pages/AutoAccounting/TestRange.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/tasks',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Tasks/TaskPage.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [{ label: 'tasks', path: '/tasks' }],
        },
      },
      {
        path: '/task/:id',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/Tasks/Detail.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            { label: 'tasks', path: '/tasks' },
            { label: 'task_details', path: '/task/:id' },
          ],
        },
      },
      {
        path: '/websocket',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/WebSocketComponent.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      // detail advance ship notice route page
      {
        path: '/sales_order_detail/:id',
        component: () => import(/* webpackChunkName: "core" */ 'src/pages/SalesOrderDetail/main_details.vue'),
        meta: {
          requiresAuth: true,
          primary_breadcrumbs: [
            {
              label: 'advance_shipment_dashboard',
              path: '/asn_shipment/dashboard',
            },
            { label: 'sales_order_detail', path: '/sales_order_detail/:id' },
          ],
        },
      },
      {
        path: '/demo_status',
        component: () =>
          import(/* webpackChunkName: "core" */ 'src/components/SupplierApprovalStatus/demo.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/performance_report',
        component: () => import(/* webpackChunkName: "reports" */ 'src/pages/Report/Performance.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    ],
  },
  {
    path: '/sales_order_detail/:id/share',
    component: () => import(/* webpackChunkName: "core" */ 'src/pages/SalesOrderDetail/main_details.vue'),
    beforeEnter: (to: any, from: any, next: any) => {
      if (!to.query.token) {
        notFoundHook.execute(to, from);
        next('/not_found');
      } else {
        next();
      }
    },
  },
  {
    path: '/supplier_invoice/share/get_details',
    component: () => import(/* webpackChunkName: "core" */ 'src/pages/Invoice/page/details.vue'),
    beforeEnter: (to: any, from: any, next: any) => {
      if (!to.query.token) {
        notFoundHook.execute(to, from);
        next('/not_found');
      } else {
        next();
      }
    },
  },
  {
    path: '/document',
    component: () => import(/* webpackChunkName: "core" */ 'layouts/NoFooterLayout.vue'),
    children: [],
  },
  {
    path: '/fields_sep_comp/:id',
    component: () => import(/* webpackChunkName: "validation" */ 'layouts/FieldsSepLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "validation" */ 'src/components/MediFieldsView.vue'),
      },
    ],
  },
  {
    path: '/fields_sep_comp_v3/:id',
    component: () => import(/* webpackChunkName: "validation" */ 'layouts/FieldsSepLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/MediOrder/MediFieldsView_v3.vue'),
      },
    ],
  },
  {
    path: '/simple_fields_comp/:id',
    component: () => import(/* webpackChunkName: "validation" */ 'layouts/FieldsSepLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/ValidationScreen/fields-view.vue'),
      },
    ],
  },
  {
    path: '/show_pdf/:id',
    component: () => import(/* webpackChunkName: "validation" */ 'layouts/SepPDFLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "validation" */ 'pages/ValidationScreen/pdf-view.vue'),
      },
    ],
  },
  {
    path: '/ocr-document/:id/:page_number',
    component: () => import(/* webpackChunkName: "validation" */ 'layouts/OcrLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "validation" */ 'src/pages/OcrDocument/OcrDocument.vue'),
      },
    ],
  },
  {
    path: '/not_found',
    component: () => import(/* webpackChunkName: "errors" */ 'layouts/ErrorRouteLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "errors" */ 'src/pages/Errors/Error404.vue'),
        meta: {
          hook: 'notFoundHook',
        },
      },
    ],
  },
  {
    path: '/network_error',
    // Network-down page always uses PublicLayout (no SideMenu) — rendering
    // the authenticated shell when the backend is unreachable would trigger
    // API calls that just fail more loudly.
    component: () => import(/* webpackChunkName: "errors" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "errors" */ 'src/pages/Errors/NetworkError.vue'),
      },
    ],
  },

  // Token-based auto-login route (must be before catch-all)
  {
    path: '/token-login',
    component: () => import(/* webpackChunkName: "auth" */ 'layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "auth" */ 'src/pages/User/TokenLogin.vue'),
        meta: {
          requiresAuth: false,
        },
      },
    ],
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import(/* webpackChunkName: "errors" */ 'layouts/ErrorRouteLayout.vue'),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "errors" */ 'src/pages/Errors/Error404.vue'),
        beforeEnter: (to: any, from: any, next: any) => {
          // Authenticated users get the "notFoundHook" side-effects (Faro
          // breadcrumb etc). Unauthenticated users see the minimal 404 page
          // directly — no redirect to /login, the Error page shows a
          // "Go to login" button itself.
          const tokenDetail: any = LocalStorage.getItem('token_details');
          if (tokenDetail) {
            notFoundHook.execute(to, from);
          }
          next();
        },
      },
    ],
  },
];

export default routes;
